import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { RotateCcw, SlidersHorizontal, ToggleLeft } from "@fleet/icons";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
  TextField,
} from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { appConfigQueryKeys, type components } from "@habits/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminConfig = components["schemas"]["AdminConfigResponse"];
type OperationalSettings = components["schemas"]["OperationalSettings"];

/**
 * Read path for each logical key inside `OperationalSettings`.
 *
 * ONLY map that needs touching when adding a numerical knob: rest of
 * panel renders from catalog served by backend.
 */
const READ: Record<string, (s: OperationalSettings) => number> = {
  "limits.rate_limit_rpm": (s) => s.limits.rateLimitRpm,
  "limits.avatar_max_upload_bytes": (s) => s.limits.avatarMaxUploadBytes,
  "limits.avatar_max_uploads_per_hour": (s) => s.limits.avatarMaxUploadsPerHour,
  "limits.habits_max_page_size": (s) => s.limits.habitsMaxPageSize,
  "retention.trash_purge_days": (s) => s.retention.trashPurgeDays,
  "retention.account_deletion_grace_days": (s) => s.retention.accountDeletionGraceDays,
  "retention.habit_version_keep": (s) => s.retention.habitVersionKeep,
  "retention.audit_log_days": (s) => s.retention.auditLogDays,
  "security.login_max_failed_attempts": (s) => s.security.loginMaxFailedAttempts,
  "security.login_lockout_secs": (s) => s.security.loginLockoutSecs,
  "streak.protector_inventory_cap": (s) => s.streak.protectorInventoryCap,
  "streak.protector_earned_per_day_cap": (s) => s.streak.protectorEarnedPerDayCap,
  "streak.protector_rescue_window_days": (s) => s.streak.protectorRescueWindowDays,
  "reminders.max_scheduled": (s) => s.reminders.maxScheduled,
  "reminders.foreground_fire_window_min": (s) => s.reminders.foregroundFireWindowMin,
  "adcaps.interstitial_per_session": (s) => s.adcaps.interstitialPerSession,
  "adcaps.interstitial_per_day": (s) => s.adcaps.interstitialPerDay,
  "adcaps.rewards_per_day_per_type": (s) => s.adcaps.rewardsPerDayPerType,
  "adcaps.offers_per_day_per_placement": (s) => s.adcaps.offersPerDayPerPlacement,
};

const BOOL_READ: Record<string, (s: OperationalSettings) => boolean> = {
  "modules.p2p_enabled": (s) => s.modules.p2pEnabled,
  "modules.avatar_uploads_enabled": (s) => s.modules.avatarUploadsEnabled,
};

/** Panel groups, in the order they are rendered. */
const GROUPS = ["limits", "retention", "security", "streak", "reminders", "adcaps"] as const;

/**
 * Admin panel for operational configuration (CONFIG_OVER_CODE).
 *
 * Hot-edits the SAME source as the environment: each change creates a persisted
 * override (`app_config`) that supersedes the deployment default; "Reset to env"
 * deletes the override and returns control to the env (or, if there is no env,
 * to the code default). None of this requires a redeploy.
 *
 * For each key, the panel shows the EFFECTIVE value, its ORIGIN (env/default or
 * override), and the corresponding environment variable — so there are not two
 * vocabularies: `AVATAR_MAX_UPLOAD_MB` (env, megabytes) and
 * `limits.avatar_max_upload_bytes` (override, bytes) are THE SAME option, and
 * the panel states this explicitly in the unit label.
 */
export function AppConfigManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("settings.write");

  const query = createApiQuery<AdminConfig, ErrorModel>(
    () => appConfigQueryKeys.admin(),
    async () => api.GET("/api/admin/config", {}),
    () => ({ staleTime: 5000 }),
  );

  const upsertMutation = createApiMutation(async (vars: { key: string; value: unknown }) =>
    api.POST("/api/admin/config", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { key: string }) =>
    api.DELETE("/api/admin/config/{key}", { params: { path: { key: vars.key } } }),
  );

  const resolved = () => query.data?.resolved;
  const envDefaults = () => query.data?.envDefaults;
  const overrides = createMemo(() => query.data?.overrides ?? []);
  const overriddenKeys = createMemo(() => new Set(overrides().map((o) => o.key)));
  const catalog = createMemo(() => query.data?.catalog ?? []);

  const refetch = () => {
    void query.refetch();
  };

  const saveOverride = (key: string, value: unknown) => {
    upsertMutation.mutate(
      { key, value },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting saved — applied live`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not save the setting`), "error");
        },
      },
    );
  };

  const resetOverride = (key: string) => {
    deleteMutation.mutate(
      { key },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting reset to its environment default`), "success");
        },
      },
    );
  };

  const groupTitle = (group: string): string => {
    switch (group) {
      case "limits": {
        return t(msg`Operational limits`);
      }
      case "retention": {
        return t(msg`Retention & purge`);
      }
      case "security": {
        return t(msg`Login security`);
      }
      case "streak": {
        return t(msg`Streak protector caps`);
      }
      case "reminders": {
        return t(msg`Reminder thresholds`);
      }
      default: {
        return t(msg`Ad frequency caps`);
      }
    }
  };

  const groupHint = (group: string): string => {
    switch (group) {
      case "streak": {
        return t(
          msg`Changing these caps decides what can be rescued from now on. It never re-judges past days: whether a day was scheduled is resolved against the definition that was live that day, and days already rescued stay rescued.`,
        );
      }
      case "adcaps": {
        return t(
          msg`These caps can only lower how often the app interrupts someone — the platform ceiling cannot be raised from here. They must stay consistent with the store listing's advertising declaration.`,
        );
      }
      default: {
        return "";
      }
    }
  };

  const numericKeys = (group: string) =>
    catalog().filter((spec) => spec.kind === "int" && spec.key.startsWith(`${group}.`));

  return (
    <div class="space-y-6" data-testid="app-config-manager">
      <div class="flex items-center gap-3">
        <SlidersHorizontal class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Operational config`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Limits, retention, login policy, streak protector caps, reminder thresholds, ad frequency and module toggles. Environment variables are the deployment defaults; every change here overrides them live, with no redeploy.`,
            )}
          </p>
        </div>
      </div>

      {/* «No hay respuesta» ≠ «no hay nada» (P23/CC-60 · I3). */}
      <Show when={query.isError}>
        <QueryErrorState testId="admin-config-error" compact onRetry={() => void query.refetch()} />
      </Show>

      <Show when={!query.isError}>
        <Show
          when={!query.isLoading && resolved()}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-24 w-full rounded-xl" />
              <Skeleton class="h-48 w-full rounded-xl" />
            </div>
          }
        >
          {/* ── Grupos numéricos ─────────────────────────────────────── */}
          <For each={GROUPS}>
            {(group) => (
              <Card data-testid={`config-group-${group}`}>
                <CardHeader>
                  <CardTitle>{groupTitle(group)}</CardTitle>
                  <Show when={groupHint(group)}>
                    <p class="text-xs text-muted-foreground">{groupHint(group)}</p>
                  </Show>
                </CardHeader>
                <CardContent class="grid gap-4 sm:grid-cols-2">
                  <For each={numericKeys(group)}>
                    {(spec) => {
                      const read = READ[spec.key];
                      const effective = () => {
                        const s = resolved();
                        return read && s ? read(s) : 0;
                      };
                      const fromEnv = () => {
                        const s = envDefaults();
                        return read && s ? read(s) : 0;
                      };
                      const isOverridden = () => overriddenKeys().has(spec.key);
                      return (
                        <div class="space-y-1">
                          <div class="flex items-center justify-between gap-2">
                            <Label for={`config-${spec.key}`} class="text-xs font-mono">
                              {spec.key}
                            </Label>
                            <Show
                              when={isOverridden()}
                              fallback={
                                <Badge variant="secondary" data-testid={`origin-${spec.key}`}>
                                  {t(msg`env / default`)}
                                </Badge>
                              }
                            >
                              <Badge data-testid={`origin-${spec.key}`}>{t(msg`override`)}</Badge>
                            </Show>
                          </div>
                          <TextField
                            id={`config-${spec.key}`}
                            type="number"
                            value={String(effective())}
                            disabled={!canWrite()}
                            data-testid={`config-value-${spec.key}`}
                            onChange={(e) => {
                              const raw = e.currentTarget.value.trim();
                              const parsed = Number.parseInt(raw, 10);
                              if (Number.isNaN(parsed) || parsed === effective()) {
                                return;
                              }
                              saveOverride(spec.key, parsed);
                            }}
                          />
                          <p class="text-[11px] text-muted-foreground">
                            {/* Unidad SIEMPRE explícita: el env y el override
                                pueden hablar en unidades distintas (MB vs bytes)
                                y eso tiene que estar escrito, no adivinado. */}
                            {spec.unit} ·{" "}
                            <span class="font-mono">
                              {spec.env}
                              {spec.envUnit ? ` (${spec.envUnit})` : ""}
                            </span>{" "}
                            = {fromEnv()}
                          </p>
                          <Show when={isOverridden()}>
                            <button
                              type="button"
                              class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                              disabled={!canWrite()}
                              data-testid={`reset-${spec.key}`}
                              onClick={() => {
                                resetOverride(spec.key);
                              }}
                            >
                              <RotateCcw class="h-3 w-3" />
                              {t(msg`Reset to env`)}
                            </button>
                          </Show>
                        </div>
                      );
                    }}
                  </For>
                </CardContent>
              </Card>
            )}
          </For>

          {/* ── Toggles de módulo ────────────────────────────────────── */}
          <Card data-testid="config-group-modules">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <ToggleLeft class="h-5 w-5 text-muted-foreground" />
                {t(msg`Modules`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <For each={catalog().filter((spec) => spec.kind === "bool")}>
                {(spec) => {
                  const read = BOOL_READ[spec.key];
                  const checked = () => {
                    const s = resolved();
                    return read && s ? read(s) : false;
                  };
                  return (
                    <div class="flex items-center justify-between gap-4">
                      <div class="min-w-0">
                        <Label class="font-mono text-xs">{spec.key}</Label>
                        <p class="text-[11px] text-muted-foreground font-mono">{spec.env}</p>
                      </div>
                      <div class="flex items-center gap-3">
                        <Show when={overriddenKeys().has(spec.key)}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            disabled={!canWrite()}
                            data-testid={`reset-${spec.key}`}
                            onClick={() => {
                              resetOverride(spec.key);
                            }}
                          >
                            <RotateCcw class="h-3 w-3" />
                            {t(msg`Reset to env`)}
                          </button>
                        </Show>
                        <span data-testid={`config-switch-${spec.key}`}>
                          <Switch
                            checked={checked()}
                            disabled={!canWrite() || upsertMutation.isPending}
                            onCheckedChange={(v) => {
                              saveOverride(spec.key, v);
                            }}
                          />
                        </span>
                      </div>
                    </div>
                  );
                }}
              </For>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
